/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import Service, { service } from '@ember/service';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import StaffAccessLevel from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import LincStaffAccess from '@linc-technologies/linced-common/enums/roles/linc-staff-access';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';

export default class RolesService extends Service {
  @service
  session;

  @service
  permissions;

  @computed('session.scopes.[]')
  get isLincStaff() {
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  // currentUserIsRestricted provides a shortcut to checking whether
  // the current user has been assigned the "Restricted" Page Access Level.
  @computed('session.person.roles.[]')
  get currentUserIsRestricted() {
    return this.has(StaffAccessLevel.RESTRICTED);
  }

  @computed('session.person.roles.[]', 'isLincStaff')
  get hasLearnerMergeAccess() {
    if (!this.isLincStaff) {
      return false;
    }
    return this.has(LincStaffAccess.LEARNER_MERGE);
  }

  @computed('session.person.roles.[]', 'isLincStaff')
  get hasCaregiverMergeAccess() {
    // Tenant Admin has caregiver merge access
    if (this.permissions.equal('urn:linced:organisation:role:admin')) {
      return true;
    }
    if (!this.isLincStaff) {
      return false;
    }
    return this.has(LincStaffAccess.CAREGIVER_MERGE);
  }

  @computed('session.person.roles.[]', 'isLincStaff')
  get hasRestrictedImportAccess() {
    if (!this.isLincStaff) {
      return false;
    }
    return this.has(LincStaffAccess.RESTRICTED_IMPORT);
  }

  // isPrincipalOrDelegate determines whether the current user is either a
  // Principal or Principal's Delegate.
  @computed('session.{scopes.[],person.roles.[]}')
  get isPrincipalOrDelegate() {
    return this.hasAny(Staff.PRINCIPAL, Staff.PRINCIPAL_S_DELEGATE);
  }

  // canManageStaffPermissions determines whether the current user is allowed
  // to manage staff members' permissions.
  @computed('session.{scopes.[],person.roles.[]}')
  get canManageStaffPermissions() {
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Hero staff can always manage staff permissions.
      return true;
    }
    return (
      this.hasAny(Staff.PRINCIPAL, Staff.PRINCIPAL_S_DELEGATE) ||
      this.permissions.meet(Scopes.TENANT_ADMIN)
    );
  }

  // canPinPost returns true if the current user can pin posts to the top
  // of a feed
  @computed('session.scopes.[]')
  get canPinPost() {
    // Admins can freely pin and unpin posts
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      return true;
    }
    return this.has(Staff.CAN_PIN_POSTS);
  }

  // has determines whether the current user has the specified role.
  has(role) {
    return this.session?.person?.roles?.includes(role) ?? false;
  }

  // hasAny determines whether the current user has any of the specified roles.
  hasAny(...roles) {
    switch (roles.length) {
      case 0:
        return false;
      case 1:
        return this.has(roles[0]);
    }
    return (
      this.session?.person?.roles?.any((role) => {
        return roles.includes(role);
      }) ?? false
    );
  }
}
